/**
 * Sets the viewport height CSS custom property for mobile browsers.
 * This ensures proper layout on mobile devices where the address bar can affect viewport height.
 *
 * Usage:
 * - Call setupViewportHeight() in ngOnInit()
 * - Call cleanupViewportHeight() in ngOnDestroy()
 */

let resizeHandler: (() => void) | null = null;

/**
 * Calculates and sets the --vh CSS custom property based on the actual viewport height.
 * On mobile devices, subtracts a safety margin to ensure navigation elements remain visible.
 */
function setViewportHeight(): void {
  return; // desables because of issues with height calculation in iframes, needs further investigation

  // Detect if we're on mobile
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  // Detect if we're in an iframe
  const inIframe = window.self !== window.top;

  // Use innerHeight — it tracks address bar changes but does NOT shrink when keyboard appears,
  // preventing the layout from collapsing and the navigation button from jumping up.
  let vh = window.innerHeight;

  // On mobile, subtract additional space to ensure navigation is visible
  // This accounts for browser chrome/address bar behavior
  if (isMobile) {
    // If in iframe, use larger margin as mobile browsers may have additional chrome
    if (inIframe) {
      vh = vh - 60; // Larger margin for iframe context on mobile
    }
  }

  document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
}

/**
 * Initializes viewport height tracking.
 * Call this in your component's ngOnInit().
 */
export function setupViewportHeight(): void {
  resizeHandler = setViewportHeight.bind(null);

  // Set initial value
  setViewportHeight();

  // Listen for resize events
  window.addEventListener('resize', resizeHandler);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', resizeHandler);
  }
}

/**
 * Cleans up viewport height event listeners.
 * Call this in your component's ngOnDestroy().
 */
export function cleanupViewportHeight(): void {
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', resizeHandler);
    }
    resizeHandler = null;
  }
}
